import { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';

interface BudgetSliderProps {
  minBudget: number;
  maxBudget: number;
  currentTotal: number;
  currency: string;
  onBudgetChange?: (min: number, max: number) => void;
  className?: string;
}

export default function BudgetSlider({
  minBudget,
  maxBudget,
  currentTotal,
  currency = 'USD',
  onBudgetChange,
  className = '',
}: BudgetSliderProps) {
  const [localMin, setLocalMin] = useState(minBudget);
  const [localMax, setLocalMax] = useState(maxBudget);
  const [isEditing, setIsEditing] = useState(false);

  // Calculate budget status
  const budgetUsed = currentTotal;
  const budgetAvailable = localMax - currentTotal;
  const budgetPercentage = localMax > 0 ? (currentTotal / localMax) * 100 : 0;

  const getBudgetStatus = () => {
    if (budgetPercentage >= 100) return { status: 'over', color: 'text-red-600', bg: 'bg-red-500' };
    if (budgetPercentage >= 80) return { status: 'warning', color: 'text-amber-600', bg: 'bg-amber-500' };
    return { status: 'ok', color: 'text-green-600', bg: 'bg-green-500' };
  };

  const { status, color, bg } = getBudgetStatus();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleApply = useCallback(() => {
    if (onBudgetChange) {
      onBudgetChange(localMin, localMax);
    }
    setIsEditing(false);
  }, [localMin, localMax, onBudgetChange]);

  const handleCancel = () => {
    setLocalMin(minBudget);
    setLocalMax(maxBudget);
    setIsEditing(false);
  };

  // Debounced auto-apply when not editing
  useEffect(() => {
    if (!isEditing && onBudgetChange) {
      onBudgetChange(minBudget, maxBudget);
    }
  }, [minBudget, maxBudget, isEditing, onBudgetChange]);

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Budget</span>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Budget Status */}
      <div className="p-4">
        {!isEditing ? (
          /* Display Mode */
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-500">Spent</span>
                <span className={`font-medium ${color}`}>
                  {formatCurrency(currentTotal)} / {formatCurrency(localMax)}
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${bg} transition-all duration-500 ease-out`}
                  style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">0%</span>
                <span className="text-xs text-gray-400">100%</span>
              </div>
            </div>

            {/* Status Indicator */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              status === 'over' ? 'bg-red-50' : status === 'warning' ? 'bg-amber-50' : 'bg-green-50'
            }`}>
              {status === 'over' && (
                <>
                  <TrendingUp className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-700">
                    Over budget by {formatCurrency(Math.abs(budgetAvailable))}
                  </span>
                </>
              )}
              {status === 'warning' && (
                <>
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    {formatCurrency(budgetAvailable)} remaining
                  </span>
                </>
              )}
              {status === 'ok' && (
                <>
                  <TrendingDown className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700">
                    {formatCurrency(budgetAvailable)} remaining
                  </span>
                </>
              )}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Total Budget</p>
                <p className="font-semibold text-gray-900">{formatCurrency(localMax)}</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Spent</p>
                <p className={`font-semibold ${color}`}>{formatCurrency(currentTotal)}</p>
              </div>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <div className="space-y-4">
            {/* Max Budget Slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Maximum Budget
                </label>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(localMax)}
                </span>
              </div>
              <input
                type="range"
                min={Math.max(currentTotal, 100)}
                max={Math.max(currentTotal * 2, 10000)}
                step={100}
                value={localMax}
                onChange={(e) => setLocalMax(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-gray-400">
                  {formatCurrency(Math.max(currentTotal, 100))}
                </span>
                <span className="text-xs text-gray-400">
                  {formatCurrency(Math.max(currentTotal * 2, 10000))}
                </span>
              </div>
            </div>

            {/* Quick Select Buttons */}
            <div>
              <label className="text-xs text-gray-500 mb-2 block">Quick Select</label>
              <div className="flex gap-2">
                {[1000, 2500, 5000, 10000].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setLocalMax(amount)}
                    className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-colors ${
                      localMax === amount
                        ? 'bg-primary-50 border-primary-200 text-primary-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {formatCurrency(amount)}
                  </button>
                ))}
              </div>
            </div>

            {/* Budget Impact */}
            <div className={`p-3 rounded-lg ${
              localMax < currentTotal ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-2">
                {localMax < currentTotal ? (
                  <>
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-700">
                      Budget is below current spending
                    </span>
                  </>
                ) : (
                  <>
                    <DollarSign className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {formatCurrency(localMax - currentTotal)} remaining
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
